import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Row, Col, Spinner, Button, Modal, Form } from "react-bootstrap";
import logo from "../../images/logo.png";
import NavbarWithDrawer from "../../ComponentsWeb/Sub/NavbarWithDrawer";
import Footer from "../../ComponentsWeb/Sub/Footer";
import { chat } from "../../const";
import { socketEmit, socketOn } from "../../Socket";
import moment from "moment";
import socket from "../../socketInstance";
import { allApi, postApi, postApiFile } from "../../helpers/apiStructure";
import LinkifiedText from "../../Components/LinkifiedText";
import BannerSlider from "../../Components/BannerSlider";
import supportIcon from "../../images/customer-care-247.png";

const customerBannerDefaults = {
  customer_top_banner_text: "",
  customer_top_banner_text_url: "",
  customer_marquee_text: "",
  customer_bottom_banner_text: "",
  customer_bottom_banner_text_url: "",
  customer_support_link: "",
};

const normalizeBannerUrl = (url = "") => {
  const trimmedUrl = String(url || "").trim();

  if (!trimmedUrl) {
    return "";
  }

  if (/^https?:\/\//i.test(trimmedUrl)) {
    return trimmedUrl;
  }

  return `https://${trimmedUrl}`;
};

const getUserRowId = (item) => item?.userId || item?.sender || item?.id || "";

const mergeUsersById = (previousUsers, incomingUsers) => {
  const mergedUsers = new Map();

  previousUsers.forEach((user) => {
    const userId = getUserRowId(user);
    if (userId) {
      mergedUsers.set(userId, user);
    }
  });

  incomingUsers.forEach((user) => {
    const userId = getUserRowId(user);
    if (!userId) {
      return;
    }

    const existingUser = mergedUsers.get(userId) || {};
    mergedUsers.set(userId, {
      ...existingUser,
      ...user,
      lastMessage: user?.lastMessage || existingUser?.lastMessage,
    });
  });

  return Array.from(mergedUsers.values());
};

const getMessageTypePreview = (messageType) => {
  switch (messageType) {
    case "image":
      return "Photo";
    case "sticker":
      return "Sticker";
    case "video":
      return "Video";
    case "audio":
      return "Audio";
    case "file":
      return "Attachment";
    default:
      return "";
  }
};

const getChatPreviewText = (message, maxLength = 60) => {
  const content = String(message?.content || "")
    .replace(/\s+/g, " ")
    .trim();

  const fallbackPreview = getMessageTypePreview(message?.messageType);
  const previewText = content || fallbackPreview;

  if (!previewText) {
    return "";
  }

  if (previewText.length <= maxLength) {
    return previewText;
  }

  return `${previewText.slice(0, maxLength).trim()}...`;
};

const CHAT_NAV_KEY = "chatUsersPaginationState";
const LONG_PRESS_DURATION_MS = 450;
const MENU_WIDTH = 160;
const MENU_HEIGHT = 112;
let pendingNavRestore = false;
const visitedGroupMsgIds = new Map(); // groupId → messageId that was visible when opened

const ChatUsers = ({ socketReady }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const scrollRestoredRef = useRef(false);
  const skipNextResetRef = useRef(false);

  useEffect(() => {
    pendingNavRestore = false;
  }, []);

  const queryParameters = new URLSearchParams(window.location.search);
  let agentList = queryParameters.get("agent");
  const shouldOpenCreateGroup = queryParameters.get("createGroup") === "true";

  const getInitialShowArchived = () => {
    if (!pendingNavRestore) return false;
    try {
      const saved = JSON.parse(sessionStorage.getItem(CHAT_NAV_KEY));
      if (saved?.showArchived) return true;
    } catch {}
    return false;
  };

  const getInitialPage = () => {
    if (!pendingNavRestore) return 1;
    try {
      const saved = JSON.parse(sessionStorage.getItem(CHAT_NAV_KEY));
      if (saved?.page) {
        skipNextResetRef.current = true;
        return saved.page;
      }
    } catch {}
    return 1;
  };

  const getInitialTab = () => {
    if (!pendingNavRestore) return "all";
    try {
      const saved = JSON.parse(sessionStorage.getItem(CHAT_NAV_KEY));
      if (saved?.tab) return saved.tab;
    } catch {}
    return "all";
  };

  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(getInitialPage);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [groupUsers, setGroupUsers] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [groupTopic, setGroupTopic] = useState("");
  const [groupImage, setGroupImage] = useState(null);
  const [groupImagePreview, setGroupImagePreview] = useState("");
  const [editingGroupId, setEditingGroupId] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [groupLoading, setGroupLoading] = useState(false);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [showArchived, setShowArchived] = useState(getInitialShowArchived);
  const [archivedGroupIds, setArchivedGroupIds] = useState(new Set());
  const [pinnedGroupIds, setPinnedGroupIds] = useState(new Set());
  const [chatMenu, setChatMenu] = useState(null);
  const longPressTimerRef = useRef(null);
  const longPressDidFireRef = useRef(false);
  const [customerBannerSettings, setCustomerBannerSettings] = useState(
    customerBannerDefaults
  );
  const userSlug = localStorage.getItem("slug");
  const isCustomer = userSlug === "customer";
  const canCreateGroup = (userSlug === "team_leader" || userSlug === "branch_leader");
  const canDeleteGroup = userSlug === "admin";
  const canManageGroups = canCreateGroup || canDeleteGroup;
  const bottomBannerUrl = normalizeBannerUrl(
    customerBannerSettings?.customer_bottom_banner_text_url
  );
  const supportLinkUrl = normalizeBannerUrl(
    customerBannerSettings?.customer_support_link
  );

  if(!agentList && userSlug === "branch_leader") {
    agentList = "true";
  }
  
  

  const baseUrl = `${window.location.protocol}//${window.location.host}`;
  // useEffect(() => {
  //   const fetchUsers = async () => {
  //     try {
  //       const response = await socketEmit("users_chat_with_me", {
  //         agentask: agentList === "true" ? 1 : 0,
  //         page,
  //       });
  //       console.log("✅ users_chat_with_me", response);
  //       setUsers(response?.data || []);
  //     } catch (err) {
  //       console.error("Socket emit error:", err);
  //     } finally {
  //       setLoading(false);
  //     }
  //   };
  //   if (socketReady) {
  //     fetchUsers();
  //   }
  // }, [socketId, socketReady, location]);
  useEffect(() => {
    if (skipNextResetRef.current) {
      skipNextResetRef.current = false;
      return;
    }
    sessionStorage.removeItem(CHAT_NAV_KEY);
    scrollRestoredRef.current = false;
    setUsers([]);
    setPage(1);
    setTotalPages(0);
    setActiveTab("all");
    setShowArchived(false);
  }, [location]);

  useEffect(() => {
    if (canCreateGroup && shouldOpenCreateGroup) {
      openGroupModal();
      const nextQueryParameters = new URLSearchParams(location.search);
      nextQueryParameters.delete("createGroup");
      navigate(
        `${location.pathname}${nextQueryParameters.toString() ? `?${nextQueryParameters.toString()}` : ""}`,
        { replace: true }
      );
    }
  }, [canCreateGroup, shouldOpenCreateGroup, location.pathname, location.search, navigate]);

  useEffect(() => {
    const fetchCustomerBannerSettings = async () => {
      try {
        const response = await postApi(
          `${baseUrl}/api/settings/forcustomer`,
          "post",
          { filter: "customer_banner" }
        );
        setCustomerBannerSettings({
          ...customerBannerDefaults,
          ...(response?.setting || {}),
        });
      } catch (error) {
        console.error("Customer banner settings error:", error);
        setCustomerBannerSettings(customerBannerDefaults);
      }
    };

    if (isCustomer) {
      fetchCustomerBannerSettings();
    }
  }, [baseUrl, isCustomer]);

  const fetchGroups = async () => {
    try {
      const response = await allApi(`${baseUrl}/api/chat-groups`, "get");
      const myId = localStorage.getItem("userid");
      const data = (response?.data || []).map((group) => {
        const visitedMsgId = visitedGroupMsgIds.get(group?.id);
        const fetchedMsgId = group.lastMessage?.id || group.lastMessage?._id;
        if (!visitedMsgId || !group.lastMessage || String(fetchedMsgId) !== String(visitedMsgId)) return group;
        const readBy = group.lastMessage.readBy || [];
        const alreadyIn = readBy.some((id) => String(id?.id || id?._id || id) === myId);
        if (alreadyIn) return group;
        return { ...group, lastMessage: { ...group.lastMessage, readBy: [...readBy, myId] } };
      });
      setGroups(data);
      setArchivedGroupIds(new Set(data.filter((g) => g?.isArchived).map((g) => g?.id)));
      setPinnedGroupIds(new Set(data.filter((g) => g?.isPinned).map((g) => g?.id)));
    } catch (error) {
      console.error("Group list error:", error);
      setGroups([]);
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const response = await socketEmit("users_chat_with_me", {
          agentask: agentList === "true" ? 1 : 0,
          page,
          filter: activeTab,
        });

        const newUsers = response?.data || [];
        setUsers(newUsers);
        if (response?.total != null && response?.pageSize) {
          setTotalPages(Math.ceil(response.total / response.pageSize));
        }

      } catch (err) {
        console.error("Socket emit error:", err);
      } finally {
        setLoading(false);
      }
    };

    if (socketReady) {
      fetchUsers();
      fetchGroups();
    }
  }, [socketReady, page, agentList, location, activeTab]);

  // Restore scroll position after back-navigation fetch completes
  useEffect(() => {
    if (scrollRestoredRef.current || !users.length) return;
    try {
      const saved = JSON.parse(sessionStorage.getItem(CHAT_NAV_KEY));
      if (saved?.scrollY) {
        setTimeout(() => window.scrollTo({ top: saved.scrollY, behavior: "instant" }), 60);
        scrollRestoredRef.current = true;
      }
    } catch {}
  }, [users]);

  const saveNavState = () => {
    try {
      pendingNavRestore = true;
      sessionStorage.setItem(CHAT_NAV_KEY, JSON.stringify({ page, scrollY: window.scrollY, tab: activeTab, showArchived }));
    } catch {}
  };

  const openGroupModal = async () => {
    setShowGroupModal(true);
    setGroupLoading(true);
    setEditingGroupId("");
    setGroupName("");
    setGroupTopic("");
    setGroupImage(null);
    setGroupImagePreview("");
    setSelectedMembers([]);

    try {
      const response = await postApi(`${baseUrl}/api/teamleaders`, "post", {
        page: 1,
      });

      const availableUsers = (response?.list || []).filter(
        (user) => user?.id !== localStorage.getItem("userid") && user?.status !== false
      );

      setGroupUsers(availableUsers);
    } catch (error) {
      console.error("Group users fetch error:", error);
      setGroupUsers([]);
    } finally {
      setGroupLoading(false);
    }
  };

  const closeGroupModal = () => {
    setShowGroupModal(false);
    setGroupUsers([]);
    setGroupName("");
    setGroupTopic("");
    setGroupImage(null);
    setGroupImagePreview("");
    setEditingGroupId("");
    setSelectedMembers([]);
    setCreatingGroup(false);
  };

  const toggleGroupMember = (userId) => {
    setSelectedMembers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const saveGroup = async () => {
    if (!groupName.trim()) {
      return;
    }

    setCreatingGroup(true);

    try {
      const response = await postApi(
        editingGroupId
          ? `${baseUrl}/api/chat-group/edit/${editingGroupId}`
          : `${baseUrl}/api/chat-group/add`,
        editingGroupId ? "put" : "post",
        {
        name: groupName,
        topic: groupTopic,
        members: selectedMembers,
        }
      );

      if (response?.status === 1) {
        const savedGroupId = response?.data?.id || editingGroupId;
        if (groupImage && savedGroupId) {
          await postApiFile(
            `${baseUrl}/api/chat-group/image/${savedGroupId}`,
            "put",
            { image: groupImage }
          );
        }
        await fetchGroups();
        closeGroupModal();
      }
    } catch (error) {
      console.error("Save group error:", error);
    } finally {
      setCreatingGroup(false);
    }
  };

  const openEditGroupModal = async (group) => {
    setShowGroupModal(true);
    setGroupLoading(true);
    setEditingGroupId(group?.id || "");
    setGroupName(group?.name || "");
    setGroupTopic(group?.topic || "");
    setGroupImage(null);
    setGroupImagePreview(group?.image || "");
    setSelectedMembers(group?.members || []);

    try {
      const response = await postApi(`${baseUrl}/api/teamleaders`, "post", {
        page: 1,
      });

      const availableUsers = (response?.list || []).filter(
        (user) => user?.id !== localStorage.getItem("userid") && user?.status !== false
      );

      setGroupUsers(availableUsers);
    } catch (error) {
      console.error("Group users fetch error:", error);
      setGroupUsers([]);
    } finally {
      setGroupLoading(false);
    }
  };

  const deleteGroup = async (group) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${group?.name || "this group"}"?`
    );

    if (!confirmed) {
      return;
    }

    try {
      const response = await allApi(`${baseUrl}/api/chat-group/${group?.id}`, "delete");

      if (response?.status === 1) {
        setGroups((prevGroups) =>
          prevGroups.filter((item) => item?.id !== group?.id)
        );
      }
    } catch (error) {
      console.error("Delete group error:", error);
    }
  };

  useEffect(() => {
    const handleNewMessage = (response) => {
      if (response?.chatGroup) {
        const incomingSenderId = response?.sender?.id || response?.sender?._id || response?.sender;
        const myId = localStorage.getItem("userid");
        const readBy = response.readBy || [];
        const isMine = String(incomingSenderId) === myId;
        const readByWithMe = isMine && !readBy.some(id => String(id?.id || id?._id || id) === myId)
          ? [...readBy, myId]
          : readBy;

        setGroups((prevGroups) =>
          prevGroups.map((group) =>
            group?.id === response?.chatGroup
              ? { ...group, lastMessage: { ...response, readBy: readByWithMe } }
              : group
          )
        );
        return;
      }

      setUsers((prevUsers) => {
        const senderId =
          typeof response?.sender === "string"
            ? response?.sender
            : response?.sender?.id || response?.sender?._id;

        // Check if sender already exists
        const userIndex = prevUsers.findIndex(
          (user) =>
            user.userId === senderId || user.sender === senderId
        );

        if (userIndex !== -1) {
          const updatedUsers = [...prevUsers];
          const updatedUser = { ...updatedUsers[userIndex] };

          updatedUser.lastMessage = response;

          updatedUsers.splice(userIndex, 1);

          return [updatedUser, ...updatedUsers];
        } else {
          return [
            {
              userId: senderId,
              sender: senderId,
              username: response?.sender?.username || response?.senderName || "New User",
              firstName: response?.sender?.firstName,
              lastName: response?.sender?.lastName,
              profile: response?.sender?.profile || response?.sender?.image || "",
              lastMessage: response,
            },
            ...prevUsers,
          ];
        }
      });
    };

    socketOn("new_message", handleNewMessage);
    return () => socket.off("new_message", handleNewMessage);
  }, []);

  const normalizedSearch = searchQuery.trim().toLowerCase();
  const [dbMessageMatches, setDbMessageMatches] = useState([]);
  const [dbContactMatches, setDbContactMatches] = useState([]);
  const [dbSearchLoading, setDbSearchLoading] = useState(false);

  // DB contact search — name + phone from server (works regardless of socket data)
  useEffect(() => {
    if (!normalizedSearch) {
      setDbContactMatches([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const result = await postApi(
          `${baseUrl}/api/users/search-chatted`,
          "post",
          { searchQuery: normalizedSearch }
        );
        setDbContactMatches(result?.data || []);
      } catch (err) {
        console.error("Contact search error:", err);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [normalizedSearch, baseUrl]);

  // DB message content search
  useEffect(() => {
    if (!normalizedSearch) {
      setDbMessageMatches([]);
      return;
    }
    const timer = setTimeout(async () => {
      setDbSearchLoading(true);
      try {
        const result = await postApi(
          `${baseUrl}/api/messages/search-all-user-chats`,
          "post",
          { searchQuery: normalizedSearch }
        );
        setDbMessageMatches(result?.data || []);
      } catch (err) {
        console.error("Search all chats error:", err);
      } finally {
        setDbSearchLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [normalizedSearch, baseUrl]);

  // Name-filtered lists (client-side, instant)
  const currentUserId = localStorage.getItem("userid");

  const handleGroupOpen = (group) => {
    saveNavState();
    const myId = localStorage.getItem("userid");
    if (group?.id) {
      const msgId = group?.lastMessage?.id || group?.lastMessage?._id || null;
      visitedGroupMsgIds.set(group.id, msgId);
    }
    setGroups((prev) =>
      prev.map((g) => {
        if (g?.id !== group?.id || !g.lastMessage) return g;
        const readBy = g.lastMessage.readBy || [];
        const alreadyRead = readBy.some((id) => String(id?.id || id?._id || id) === myId);
        if (alreadyRead) return g;
        return { ...g, lastMessage: { ...g.lastMessage, readBy: [...readBy, myId] } };
      })
    );
    navigate(
      `${chat}?group=true&groupId=${group?.id}&name=${encodeURIComponent(
        group?.name || "Group"
      )}&topic=${encodeURIComponent(group?.topic || "")}`
    );
  };

  const hasGroupUnread = (group) => {
    const msg = group?.lastMessage;
    if (!msg) return false;
    const senderId = msg.sender?.id || msg.sender?._id || msg.sender;
    if (String(senderId) === currentUserId) return false;
    const readBy = msg.readBy || [];
    return !readBy.some((id) => String(id?.id || id?._id || id) === currentUserId);
  };

  const getLongPressProps = (group) => {
    const isGroupArchived = archivedGroupIds.has(group?.id);
    const isGroupPinned = pinnedGroupIds.has(group?.id);
    const openMenu = (left, top) => {
      const clampedLeft = Math.min(left, window.innerWidth - MENU_WIDTH - 8);
      const clampedTop = Math.min(top, window.innerHeight - MENU_HEIGHT - 8);
      longPressDidFireRef.current = true;
      setChatMenu({
        id: group?.id,
        name: group?.name,
        archived: isGroupArchived,
        pinned: isGroupPinned,
        left: Math.max(8, clampedLeft),
        top: Math.max(8, clampedTop),
      });
    };
    return {
      onContextMenu: (e) => { e.preventDefault(); openMenu(e.clientX, e.clientY); },
      onTouchStart: (e) => {
        const touch = e.touches[0];
        const x = touch.clientX;
        const y = touch.clientY;
        longPressTimerRef.current = setTimeout(() => openMenu(x, y), LONG_PRESS_DURATION_MS);
      },
      onTouchMove: () => clearTimeout(longPressTimerRef.current),
      onTouchEnd: () => clearTimeout(longPressTimerRef.current),
      onTouchCancel: () => clearTimeout(longPressTimerRef.current),
    };
  };

  const toggleArchive = async (groupId, archive) => {
    const prev = new Set(archivedGroupIds);
    setChatMenu(null);
    longPressDidFireRef.current = false;
    setArchivedGroupIds((ids) => {
      const next = new Set(ids);
      if (archive) next.add(groupId);
      else next.delete(groupId);
      return next;
    });
    try {
      const resp = await postApi(`${baseUrl}/api/chat/archive`, "post", { targetId: groupId, archive });
      if (!resp?.status) setArchivedGroupIds(prev);
    } catch {
      setArchivedGroupIds(prev);
    }
  };

  const togglePin = async (groupId, pin) => {
    const prev = new Set(pinnedGroupIds);
    setChatMenu(null);
    longPressDidFireRef.current = false;
    setPinnedGroupIds((ids) => {
      const next = new Set(ids);
      if (pin) next.add(groupId);
      else next.delete(groupId);
      return next;
    });
    try {
      const resp = await postApi(`${baseUrl}/api/chat/pin`, "post", { targetId: groupId, pin });
      if (!resp?.status) setPinnedGroupIds(prev);
    } catch {
      setPinnedGroupIds(prev);
    }
  };

  // Dismiss context menu on scroll (capture phase), resize, or Escape
  useEffect(() => {
    if (!chatMenu) return;
    const close = () => { setChatMenu(null); longPressDidFireRef.current = false; };
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("scroll", close, { capture: true });
    window.addEventListener("resize", close);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", close, { capture: true });
      window.removeEventListener("resize", close);
      window.removeEventListener("keydown", onKey);
    };
  }, [chatMenu]);

  // Clean up long-press timer on unmount
  useEffect(() => () => clearTimeout(longPressTimerRef.current), []);

  const filteredGroups = (normalizedSearch
    ? groups.filter(
        (g) =>
          (g?.name || "").toLowerCase().includes(normalizedSearch) ||
          (g?.topic || "").toLowerCase().includes(normalizedSearch)
      )
    : groups
  ).filter((g) =>
    showArchived ? archivedGroupIds.has(g?.id) : !archivedGroupIds.has(g?.id)
  ).sort((a, b) => {
    const pinnedDelta = (pinnedGroupIds.has(b?.id) ? 1 : 0) - (pinnedGroupIds.has(a?.id) ? 1 : 0);
    if (pinnedDelta !== 0) return pinnedDelta;
    const aTime = a?.lastMessage?.createdAt ? new Date(a.lastMessage.createdAt).getTime() : 0;
    const bTime = b?.lastMessage?.createdAt ? new Date(b.lastMessage.createdAt).getTime() : 0;
    return bTime - aTime;
  });

  const nameFilteredUsers = normalizedSearch
    ? users.filter((item) => {
        const name = (
          item?.username ||
          `${item?.firstName || ""} ${item?.lastName || ""}`.trim()
        ).toLowerCase();
        const phone = (item?.phone || "").toLowerCase();
        return name.includes(normalizedSearch) || phone.includes(normalizedSearch);
      })
    : users;

  // Merge DB contact matches (by name/phone) that aren't already shown in name filter
  const nameFilteredIds = new Set(nameFilteredUsers.map((u) => u?.userId || u?.sender));
  const extraContactMatches = normalizedSearch
    ? dbContactMatches
        .filter((m) => !nameFilteredIds.has(m.userId))
        .map((m) => {
          // Try to get full info (lastMessage, lastSeen) from existing users list
          const existing = users.find((u) => (u?.userId || u?.sender) === m.userId);
          return existing ? { ...existing, ...m } : m;
        })
    : [];

  // Combined contacts list — newest message first
  const filteredUsers = [...nameFilteredUsers, ...extraContactMatches].sort((a, b) => {
    const aTime = a?.lastMessage?.createdAt ? new Date(a.lastMessage.createdAt).getTime() : 0;
    const bTime = b?.lastMessage?.createdAt ? new Date(b.lastMessage.createdAt).getTime() : 0;
    return bTime - aTime;
  });

  // DB message matches — exclude conversations already shown in contacts
  const nameFilteredUserIds = new Set(filteredUsers.map((u) => u?.userId || u?.sender || u?.userId));
  const nameFilteredGroupIds = new Set(filteredGroups.map((g) => g?.id));

  const msgMatchUsers = normalizedSearch
    ? dbMessageMatches.filter(
        (m) => m.type === "user" && !nameFilteredUserIds.has(m.userId)
      )
    : [];
  const msgMatchGroups = normalizedSearch
    ? dbMessageMatches.filter(
        (m) => m.type === "group" && !nameFilteredGroupIds.has(m.groupId)
      )
    : [];

  const archivedCount = groups.filter((g) => archivedGroupIds.has(g?.id)).length;
  const archivedUnreadCount = groups.filter(
    (g) => archivedGroupIds.has(g?.id) && hasGroupUnread(g)
  ).length;

  const hasMessageMatches = msgMatchUsers.length > 0 || msgMatchGroups.length > 0;
  const hasAnyResults =
    filteredGroups.length > 0 ||
    filteredUsers.length > 0 ||
    hasMessageMatches ||
    dbContactMatches.length > 0;

  return (
    <>
      <NavbarWithDrawer />
      {isCustomer && customerBannerSettings?.customer_marquee_text && (
        <div className="customer-banner-marquee customer-banner-marquee-header customer-banner-marquee-chat">
          <marquee
            behavior="default"
            className="customer-banner-marquee-track-chat"
            scrollAmount="6"
          >
            <span>{customerBannerSettings.customer_marquee_text}</span>
          </marquee>
        </div>
      )}
      <div className="container-fluid">
        <div className="p-5 mt-2 mb-5 mbl-pedding-10 pt-0">
          <div className="chat-users-searchbar-wrapper">
            {showSearch ? (
              <div className="chat-users-searchbar-active">
                <button
                  className="chat-users-searchbar-back"
                  onClick={() => { setShowSearch(false); setSearchQuery(""); }}
                >
                  <i className="bi bi-arrow-left"></i>
                </button>
                <input
                  type="text"
                  className="chat-users-searchbar-input"
                  placeholder="Search chats..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                />
                {searchQuery && (
                  <button
                    className="chat-users-searchbar-clear"
                    onClick={() => setSearchQuery("")}
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                )}
              </div>
            ) : (
              <div
                className="chat-users-searchbar-toggle"
                onClick={() => setShowSearch(true)}
              >
                <i className="bi bi-search me-2"></i>
                <span>Search...</span>
              </div>
            )}
          </div>

          {isCustomer && customerBannerSettings?.customer_top_banner_text && (
            <div className="customer-banner customer-banner-top">
              <BannerSlider
                value={customerBannerSettings.customer_top_banner_text}
                linkUrl={customerBannerSettings.customer_top_banner_text_url}
              />
            </div>
          )}
          {/* Archived entry row — main view only, hidden during search */}
          {!showArchived && !searchQuery && archivedCount > 0 && (
            <div
              className={`chat-archived-entry-row${archivedUnreadCount > 0 ? " unread-message-borader archived-unread" : " archived-border"}`}
              onClick={() => setShowArchived(true)}
            >
              <i className="bi bi-archive-fill chat-archived-entry-icon"></i>
              <span className="chat-archived-entry-label">Archived</span>
              {archivedUnreadCount > 0 && (
                <span className="chat-archived-unread-badge">{archivedUnreadCount}</span>
              )}
              <span className="chat-archived-total">{archivedCount}</span>
              <i className="bi bi-chevron-right ms-auto"></i>
            </div>
          )}

          {/* Archived view header */}
          {showArchived && (
            <div className="archived-view-header">
              <button
                className="archived-back-btn"
                onClick={() => setShowArchived(false)}
                aria-label="Back to chats"
              >
                <i className="bi bi-arrow-left"></i>
              </button>
              <span className="archived-view-title">Archived</span>
            </div>
          )}

          {filteredGroups.length > 0 ? (
            <div className="chat-groups-section">
              {!showArchived && <h5 className="chat-groups-heading">Groups</h5>}
              {filteredGroups.map((group, index) => (
                <Row
                  key={`group-${group?.id || index}`}
                  className={`mt-2 shadow-none py-2 bg-light-gray rounded align-items-center cursor-pointer chat-group-pressable${hasGroupUnread(group) ? " unread-message-borader" : ""}${pinnedGroupIds.has(group?.id) ? " chat-group-pinned" : ""}`}
                  onClick={() => {
                    if (longPressDidFireRef.current) { longPressDidFireRef.current = false; return; }
                    handleGroupOpen(group);
                  }}
                  {...getLongPressProps(group)}
                >
                  <Col md={2} sm={2} xs={2}>
                    {group?.image ? (
                      <img
                        className="profile-img"
                        src={group.image}
                        alt={group?.name || "Group"}
                      />
                    ) : (
                      <div className="profile-img d-flex align-items-center justify-content-center bg-success text-white fw-bold">
                        {(group?.name || "G").charAt(0).toUpperCase()}
                      </div>
                    )}
                  </Col>
                  <Col
                    md={canManageGroups ? 5 : 8}
                    sm={canManageGroups ? 5 : 8}
                    xs={canManageGroups ? 5 : 8}
                  >
                    <p className="mb-0 fw-bold">{group?.name}</p>
                    <p className="mb-0">
                      <LinkifiedText
                        text={
                          getChatPreviewText(group?.lastMessage) ||
                          group?.topic ||
                          "Tap to start discussion"
                        }
                      />
                    </p>
                  </Col>
                  <Col
                    className="px-0 dddd"
                    md={canManageGroups ? 2 : 2}
                    sm={canManageGroups ? 2 : 2}
                    xs={canManageGroups ? 2 : 2}
                  >
                    <p className="mb-0">
                      <b>
                        {group?.lastMessage?.createdAt &&
                          moment(group?.lastMessage?.createdAt).format("hh:mm A")}
                      </b>
                      {pinnedGroupIds.has(group?.id) && (
                        <i className="bi bi-pin-fill chat-group-pin-badge ms-1"></i>
                      )}
                    </p>
                  </Col>
                  {canManageGroups && !showArchived && (
                    <Col md={3} sm={3} xs={3} className="text-end">
                      <Button
                        variant="outline-success"
                        size="sm"
                        className={canDeleteGroup ? "me-2" : ""}
                        onClick={(event) => {
                          event.stopPropagation();
                          openEditGroupModal(group);
                        }}
                      >
                        Edit
                      </Button>
                      {canDeleteGroup && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={(event) => {
                            event.stopPropagation();
                            deleteGroup(group);
                          }}
                        >
                          Delete
                        </Button>
                      )}
                    </Col>
                  )}
                </Row>
              ))}
              {!showArchived && <hr />}
            </div>
          ) : showArchived ? (
            <div className="text-center mt-5">
              <p className="text-muted">No archived chats.</p>
            </div>
          ) : null}

          {!showArchived && (
            <div className="chat-tabs-wrapper">
              <div className="chat-tabs-bar">
                {["all", "unread", "read"].map((tab) => (
                  <button
                    key={tab}
                    className={`chat-tab-btn${activeTab === tab ? " active" : ""}`}
                    onClick={() => { setActiveTab(tab); setPage(1); }}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {!showArchived && loading ? (
            <div className="d-flex justify-content-center align-items-center">
              <Spinner animation="grow" />
            </div>
          ) : !showArchived && filteredUsers?.length > 0 ? (
            <>
              {filteredUsers.map((item, index) => (
                <Row
                  key={`user-${item?.userId || item?.sender || index}`}
                  onClick={() => {
                    saveNavState();
                    navigate(
                      `${chat}?id=${
                        item?.userId ? item?.userId : item?.sender
                      }&name=${
                        item?.userId
                          ? item?.username
                            ? item?.username
                            : `${item?.firstName} ${item?.lastName}`
                          : "New User"
                      }&img=${item?.userId ? item?.profile : ""}&time=${
                        item?.userId ? item?.lastSeen : ""
                      }&agent=${agentList ? agentList : ""}&roleSlug=${item?.roleSlug || ""}`
                    );
                  }}
                  className={`mt-2 shadow-none py-2 bg-light-gray rounded align-items-center cursor-pointer ${
                         item?.lastMessage && !item.lastMessage.isRead && item?.lastMessage?.sender !== localStorage.getItem("userid")
                            ? `unread-message-borader` : ``
                        }`}
                >
                  <Col md={2} sm={2} xs={2}>
                    <div className="profile-img-wrapper">
                      <img
                        className="profile-img"
                        src={item?.profile ? item?.profile : logo}
                        alt={item?.firstName || "user"}
                      />
                      {(item?.roleSlug === "team_leader" || item?.roleSlug === "branch_leader") && (
                        <span className="verified-badge">
                          <i className="bi bi-patch-check-fill"></i>
                        </span>
                      )}
                    </div>
                  </Col>
                  <Col md={8} sm={8} xs={8}>
                    <p className="mb-0 fw-bold">
                      {!item?.username
                        ? `New User`
                        : item?.username
                        ? item?.username
                        : `${item?.firstName} ${item?.lastName}`}
                    </p>
                    {!item?.username ? (
                      <p className="mb-0">
                        <LinkifiedText
                          text={getChatPreviewText(item)}
                        />
                      </p>
                    ) : (
                      getChatPreviewText(item?.lastMessage) && (
                        <p className="mb-0">
                          <LinkifiedText
                            text={getChatPreviewText(item?.lastMessage)}
                          />
                        </p>
                      )
                    )}
                  </Col>
                  {item?.userId !== localStorage.getItem("userid") && (
                    <Col className={`px-0 dddd`} md={2} sm={2} xs={2}>
                      <p
                        className={`mb-0 ${
                          !item?.lastMessage?.isRead && item?.lastMessage?.sender !== localStorage.getItem("userid")
                            ? `text-success` : ``
                        }`}
                      >
                        <b>
                          {item?.lastMessage?.createdAt &&
                            moment(item?.lastMessage?.createdAt).format(
                              "hh:mm A"
                            )}
                        </b>
                      </p>
                    </Col>
                  )}
                </Row>
              ))}
              {!isCustomer && totalPages > 1 && (() => {
                const delta = 2;
                const pages = [];
                for (let i = 1; i <= totalPages; i++) {
                  if (i === 1 || i === totalPages || (i >= page - delta && i <= page + delta)) {
                    pages.push(i);
                  }
                }
                const buttons = [];
                let prev = null;
                for (const p of pages) {
                  if (prev !== null && p - prev > 1) {
                    buttons.push(<span key={`ellipsis-${p}`} className="chat-page-ellipsis">…</span>);
                  }
                  buttons.push(
                    <button
                      key={p}
                      className={`chat-page-btn${page === p ? " active" : ""}`}
                      onClick={() => setPage(p)}
                      disabled={loading}
                    >
                      {p}
                    </button>
                  );
                  prev = p;
                }
                return (
                  <Row className="mt-3 mb-2">
                    <Col className="d-flex justify-content-center">
                      <div className="chat-pagination">
                        <button className="chat-page-btn" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1 || loading}>
                          <i className="bi bi-chevron-left"></i>
                        </button>
                        {buttons}
                        <button className="chat-page-btn" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages || loading}>
                          <i className="bi bi-chevron-right"></i>
                        </button>
                      </div>
                    </Col>
                  </Row>
                );
              })()}
            </>
          ) : !showArchived && !normalizedSearch ? (
            <div className="text-center mt-5 justify-content-center align-items-center">
              {activeTab !== "all" ? (
                <h4 className="text-muted w-100">No {activeTab} chats.</h4>
              ) : (
                <>
                  <h4 className="text-danger w-100">No users found to chat with.</h4>
                  <h4 className="text-success w-100">
                    Some time need to refresh{" "}
                    <Button onClick={() => window.location.reload()}>Click</Button>
                  </h4>
                </>
              )}
            </div>
          ) : null}

          {/* DB message matches section — hidden in archived view */}
          {!showArchived && normalizedSearch && (dbSearchLoading || hasMessageMatches) && (
            <div className="chat-users-msg-matches">
              <h6 className="chat-users-msg-matches-heading">Messages</h6>
              {dbSearchLoading && (
                <div className="text-center py-2">
                  <Spinner animation="border" size="sm" />
                </div>
              )}
              {msgMatchGroups.map((match, index) => (
                <Row
                  key={`msgmatch-group-${match.groupId}-${index}`}
                  className="mt-2 shadow-none py-2 bg-light-gray rounded align-items-center cursor-pointer"
                  onClick={() => { saveNavState(); navigate(`${chat}?group=true&groupId=${match.groupId}&name=${encodeURIComponent(match.name)}&topic=&searchQ=${encodeURIComponent(searchQuery)}`); }}
                >
                  <Col md={2} sm={2} xs={2}>
                    {match.image ? (
                      <img className="profile-img" src={match.image} alt={match.name} />
                    ) : (
                      <div className="profile-img d-flex align-items-center justify-content-center bg-success text-white fw-bold">
                        {(match.name || "G").charAt(0).toUpperCase()}
                      </div>
                    )}
                  </Col>
                  <Col md={8} sm={8} xs={8}>
                    <p className="mb-0 fw-bold">{match.name}</p>
                    <p className="mb-0 chat-users-match-snippet">
                      <LinkifiedText text={match.latestContent} highlight={searchQuery} />
                    </p>
                  </Col>
                  <Col md={2} sm={2} xs={2} className="px-0 text-end">
                    <small className="text-muted">{match.matchCount} match{match.matchCount !== 1 ? "es" : ""}</small>
                  </Col>
                </Row>
              ))}
              {msgMatchUsers.map((match, index) => (
                <Row
                  key={`msgmatch-user-${match.userId}-${index}`}
                  className="mt-2 shadow-none py-2 bg-light-gray rounded align-items-center cursor-pointer"
                  onClick={() => { saveNavState(); navigate(`${chat}?id=${match.userId}&name=${encodeURIComponent(match.username || "User")}&img=${encodeURIComponent(match.profile || "")}&agent=${agentList || ""}&searchQ=${encodeURIComponent(searchQuery)}`); }}
                >
                  <Col md={2} sm={2} xs={2}>
                    <img
                      className="profile-img"
                      src={match.profile || logo}
                      alt={match.username || "User"}
                    />
                  </Col>
                  <Col md={8} sm={8} xs={8}>
                    <p className="mb-0 fw-bold">{match.username || "User"}</p>
                    <p className="mb-0 chat-users-match-snippet">
                      <LinkifiedText text={match.latestContent} highlight={searchQuery} />
                    </p>
                  </Col>
                  <Col md={2} sm={2} xs={2} className="px-0 text-end">
                    <small className="text-muted">{match.matchCount} match{match.matchCount !== 1 ? "es" : ""}</small>
                  </Col>
                </Row>
              ))}
            </div>
          )}

          {/* No results at all — hidden in archived view */}
          {!showArchived && normalizedSearch && !loading && !dbSearchLoading && !hasAnyResults && (
            <div className="text-center mt-4">
              <p className="text-muted">No results for "{searchQuery}"</p>
            </div>
          )}
          {isCustomer && customerBannerSettings?.customer_bottom_banner_text && (
            <div className="customer-banner customer-banner-bottom">
              {bottomBannerUrl ? (
                <a
                  href={bottomBannerUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="customer-banner-link"
                >
                  <img
                    src={customerBannerSettings.customer_bottom_banner_text}
                    alt="Bottom banner"
                    className="customer-banner-image"
                  />
                </a>
              ) : (
                <img
                  src={customerBannerSettings.customer_bottom_banner_text}
                  alt="Bottom banner"
                  className="customer-banner-image"
                />
              )}
            </div>
          )}
        </div>
      </div>
      {isCustomer && supportLinkUrl && (
        <a
          href={supportLinkUrl}
          target="_blank"
          rel="noreferrer"
          className="customer-support-fab"
          aria-label="24/7 customer support"
        >
          <img src={supportIcon} alt="24/7 customer care" />
        </a>
      )}
      <Footer />

      {/* Context menu backdrop + menu */}
      {chatMenu && (
        <>
          <div
            className="chat-context-menu-backdrop"
            onClick={() => { setChatMenu(null); longPressDidFireRef.current = false; }}
          />
          <div
            className="chat-context-menu"
            style={{ left: chatMenu.left, top: chatMenu.top, width: MENU_WIDTH }}
          >
            <button
              className="chat-context-menu-item"
              onClick={() => togglePin(chatMenu.id, !chatMenu.pinned)}
            >
              <i className={`bi bi-${chatMenu.pinned ? "pin-fill" : "pin"} me-2`}></i>
              {chatMenu.pinned ? "Unpin" : "Pin"}
            </button>
            <button
              className="chat-context-menu-item"
              onClick={() => toggleArchive(chatMenu.id, !chatMenu.archived)}
            >
              <i className={`bi bi-${chatMenu.archived ? "arrow-up-circle" : "archive"} me-2`}></i>
              {chatMenu.archived ? "Unarchive" : "Archive"}
            </button>
          </div>
        </>
      )}

      <Modal show={showGroupModal} onHide={closeGroupModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingGroupId ? "Edit Group" : "Create Group"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Group Name</Form.Label>
            <Form.Control
              value={groupName}
              onChange={(event) => setGroupName(event.target.value)}
              placeholder="Enter group name"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Topic</Form.Label>
            <Form.Control
              value={groupTopic}
              onChange={(event) => setGroupTopic(event.target.value)}
              placeholder="Enter discussion topic"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="group-image">Group DP</Form.Label>
            <Form.Control
              id="group-image"
              type="file"
              accept="image/*"
              onChange={(event) => {
                const selectedFile = event.target.files[0];
                setGroupImage(selectedFile || null);
                setGroupImagePreview(
                  selectedFile ? URL.createObjectURL(selectedFile) : editingGroupId ? groupImagePreview : ""
                );
              }}
            />
            {groupImagePreview && (
              <img
                src={groupImagePreview}
                alt="Group preview"
                className="profile-img mt-2"
              />
            )}
          </Form.Group>

          <Form.Label>Select Members</Form.Label>
          {groupLoading ? (
            <div className="text-center py-3">
              <Spinner animation="border" />
            </div>
          ) : (
            <div className="chat-forward-users">
              {groupUsers.map((user) => {
                const fullName =
                  `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
                  "No Name";

                return (
                  <label key={user?.id} className="chat-forward-user">
                    <img
                      className="profile-img"
                      src={user?.image || logo}
                      alt={fullName}
                    />
                    <div className="chat-forward-user-copy">
                      <strong>{fullName}</strong>
                    </div>
                    <Form.Check
                      type="checkbox"
                      checked={selectedMembers.includes(user?.id)}
                      onChange={() => toggleGroupMember(user?.id)}
                    />
                  </label>
                );
              })}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeGroupModal}>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={saveGroup}
            disabled={creatingGroup || !groupName.trim()}
          >
            {creatingGroup
              ? editingGroupId
                ? "Saving..."
                : "Creating..."
              : editingGroupId
              ? "Save Changes"
              : "Create Group"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ChatUsers;
